/**
 * Who vouches on a certificate (20261020150000). Display names are free text,
 * so an issuer is never shown bare: an unverified org says so, and a person
 * always reads as "a weXare member". Pure and dependency-free, so api/og.ts
 * can word the share card with the same rule.
 */

import { BRAND_NAME } from "@/lib/brand";

/** Snapshotted at issue from organizations.verified_at. */
export type IssuerKind = "org_verified" | "org_unverified" | "person";

const KINDS: readonly IssuerKind[] = ["org_verified", "org_unverified", "person"];

/** null = the server predates issuer_kind; the issuer is then shown as stored. */
export function parseIssuerKind(raw: unknown): IssuerKind | null {
  return (KINDS as readonly unknown[]).includes(raw) ? (raw as IssuerKind) : null;
}

/** The qualifiers, from i18n in the app and in English on the share card. */
export interface IssuerQualifiers {
  unverifiedOrg: (name: string) => string; // "Google, unverified organization"
  member: (name: string) => string; // "Ana Benítez, weXare member"
  someMember: string; // "a weXare member" (no name, no handle)
}

export const ENGLISH_ISSUER_QUALIFIERS: IssuerQualifiers = {
  unverifiedOrg: (name) => `${name}, unverified organization`,
  member: (name) => `${name}, ${BRAND_NAME} member`,
  someMember: `a ${BRAND_NAME} member`,
};

/**
 * Longest free-text name a label keeps. display_name has no length limit,
 * and a long one pushed the qualifier off the card's and the PDF's lines,
 * leaving the issuer bare. Org names are capped at 80 by the database.
 */
export const MAX_PERSON_NAME = 60;

/**
 * Shortens the name part of a person label, never the " (@handle)" suffix.
 * Names have '@' stripped (certificate_person_label), so the first " (@"
 * starts the real handle.
 */
export function capPersonLabel(label: string): string {
  const at = label.indexOf(" (@");
  const name = at === -1 ? label : label.slice(0, at);
  const chars = Array.from(name);
  if (chars.length <= MAX_PERSON_NAME) return label;
  return `${chars.slice(0, MAX_PERSON_NAME - 1).join("").trimEnd()}…${at === -1 ? "" : label.slice(at)}`;
}

/** A person's label ("Ana (@ana)" or a bare name), always as a member. */
export function qualifiedPerson(name: string | null | undefined, q: IssuerQualifiers): string {
  const trimmed = name?.trim();
  return trimmed ? q.member(capPersonLabel(trimmed)) : q.someMember;
}

/** The issuer as a reader should see it. */
export function qualifiedIssuer(kind: IssuerKind | null, name: string, q: IssuerQualifiers): string {
  switch (kind) {
    case "org_verified":
      return name;
    case "org_unverified":
      return q.unverifiedOrg(name);
    case "person":
      return qualifiedPerson(name, q);
    default:
      return name;
  }
}
